import { Form, FormQuestion } from '../../API/forms/forms';
import { Answer, Report } from '../../API/reports/reports';
import { computeFormula } from '@simplifield/formula';

export const SF_QUESTION_PRODUCT_TYPES = {
  SALES: 'sales',
  COUNT: 'count',
};

export function isMediaType(question: FormQuestion): boolean {
  return ['video', 'audio', 'image'].includes(question.type);
}

export function isDocument(question: FormQuestion): boolean {
  return question.type === 'document';
}

export function isDate(question: FormQuestion): boolean {
  const firstField = question.fields[0];

  return firstField.type === 'date';
}

export function isTime(question: FormQuestion): boolean {
  const firstField = question.fields[0];

  return firstField.type === 'time';
}

export function isRadioType(
  question: FormQuestion,
  form: Form,
  report: Report,
  ctx?
): boolean {
  const firstField = question.fields[0];
  const maximum = getMaximum(question, form, report, ctx);

  return Boolean(firstField.set && maximum === 1);
}

export function isCheckboxType(
  question: FormQuestion,
  form: Form,
  report: Report,
  ctx?
): boolean {
  const firstField = question.fields[0];
  const maximum = getMaximum(question, form, report, ctx);

  return Boolean(firstField.set && maximum !== 1);
}

export function isProductScan(question: FormQuestion): boolean {
  const firstField = question.fields[0];

  return firstField.type === 'ean' && firstField.sources?.[0] === 'barcode';
}

export function isSignatureType(question: FormQuestion): boolean {
  return question.type === 'signature';
}

export function isBarcodeType(question: FormQuestion): boolean {
  const firstField = question.fields[0];

  return Boolean(firstField.sources && firstField.sources[0] === 'barcode');
}

export function isRatingType(question: FormQuestion): boolean {
  const firstField = question.fields[0];

  return Boolean(firstField?.type === 'number' && firstField?.multipleOf);
}

export function isNumberType(question: FormQuestion): boolean {
  const firstField = question.fields[0];

  return firstField?.type === 'number';
}

export function isProductQuestion(question: FormQuestion): boolean {
  return isProductCount(question) || isProductSales(question);
}

export function isProductCount(question: FormQuestion): boolean {
  return question.type === 'product-count';
}

export function isProductSales(question: FormQuestion): boolean {
  return question.type === 'product-sales';
}

export function isFormula(question: FormQuestion): boolean {
  return question.type === 'formula';
}

export function isUsers(question: FormQuestion): boolean {
  return question.type === 'users';
}

export function getQuestionTypeName(
  question: FormQuestion,
  form: Form,
  report: Report,
  ctx?
): string {
  const tests = [
    { type: 'image', test: isMediaType },
    { type: 'date', test: isDate },
    { type: 'time', test: isTime },
    { type: 'productScan', test: isProductScan },
    { type: 'questionRadio', test: isRadioType },
    { type: 'questionCheckbox', test: isCheckboxType },
    { type: 'signature', test: isSignatureType },
    { type: 'barcode', test: isBarcodeType },
    { type: 'rating', test: isRatingType },
    { type: 'number', test: isNumberType },
    { type: 'productCount', test: isProductCount },
    { type: 'productSales', test: isProductSales },
    { type: 'formula', test: isFormula },
    { type: 'document', test: isDocument },
    { type: 'users', test: isUsers },
  ];
  const typeFound = tests.reduce(
    (type, test) =>
      type || (test.test(question, form, report, ctx) ? test.type : null),
    null as unknown // prevent some weird ts error msg
  ) as string;

  return typeFound || 'simple';
}

export function getMaximum(
  question: FormQuestion,
  form: Form,
  report: Report,
  ctx?
) {
  return typeof question.maximum === 'string' && question.maximum !== 'Infinity'
    ? computeFormula(
        'number',
        question.maximum,
        {
          form: form.contents || form,
          time: Date.now.bind(Date),
        },
        {
          answers: report.contents.answers,
          ...ctx,
        }
      )
    : question.maximum;
}

export function isImageDisplay(answer: Answer): boolean {
  return ['image', 'signature'].includes(answer.type);
}

export function isDocumentDisplay(answer: Answer): boolean {
  return answer.type === 'document';
}

export function isUsersDisplay(answer: Answer): boolean {
  return answer.type === 'users';
}

export function getProductQuestionMode(question: FormQuestion) {
  return question.type === 'product-sales'
    ? SF_QUESTION_PRODUCT_TYPES.SALES
    : SF_QUESTION_PRODUCT_TYPES.COUNT;
}
